import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useAuth } from '@/providers/AuthProvider'
import { PERMISSIONS } from '@/lib/permissions'
import { RelativeTime } from '@/components/DataDisplay'
import { EmptyState, ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import {
  fetchBotOwner,
  fetchBotWebhook,
  fetchBotApps,
  fetchBotBusiness,
  fetchBotAffiliate,
  fetchBotGames,
  fetchBotStats,
  fetchBotAppSettings,
  fetchBotPreviewMedia,
  fetchBotToken,
} from './botApi'

// A5.03–A5.30 — bot-karta pastki (sub-route) ekranlari. Har biri O'Z endpointini
// yuklaydi (real `bot_platform.*` ma'lumot; SOXTA yo'q). Bo'sh natija → to'g'ri
// «bo'sh holat»; xato → qayta-urinish. Egasi/webhook/token/statistika = bot
// gateway; UserCard/UserSessions naqshi (PageHeader + orqaga havola + States).

const backLink = (id: string, label: string) => (
  <Link
    to={`/bots/${id}`}
    className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
  >
    <ChevronLeft size={13} />
    {label}
  </Link>
)

function LoadingCard() {
  return (
    <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-4 w-2/3" />
      ))}
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">{children}</div>
}

/** Yuklaydi + AbortController; xato → qayta-urinish tugmasi uchun `retry`. */
function useBotResource<T>(
  load: (id: number, signal: AbortSignal) => Promise<T>,
  botId: number,
): { loading: boolean; data: T | null; error: Error | null; retry: () => void } {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    load(botId, ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [botId, load, reload])

  return { loading, data, error, retry: () => setReload((n) => n + 1) }
}

/** Umumiy ekran-qobiq: huquq → PageHeader (orqaga havola) → loading/error/body. */
function Shell({
  screenId,
  title,
  botId,
  loading,
  error,
  retry,
  children,
}: {
  screenId: string
  title: string
  botId: string
  loading: boolean
  error: Error | null
  retry: () => void
  children: ReactNode
}) {
  const t = useT()
  const { can } = useAuth()
  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  return (
    <>
      <PageHeader title={title} screenId={screenId} actions={backLink(botId, t.bots.card)} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? <LoadingCard /> : error ? <ErrorState code={error.message} onRetry={retry} /> : children}
      </div>
    </>
  )
}

/** A5.03 — bot egasi. GET /admin/bots/:id/owner → { owner }. */
export function BotOwner() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotOwner, Number(id))
  return (
    <Shell screenId="A5.03" title={t.bots.ownerTitle} botId={id} loading={loading} error={error} retry={retry}>
      {!data ? (
        <EmptyState title={t.bots.noOwner} />
      ) : (
        <Card>
          <div className="space-y-0.5 text-sm">
            <div className="text-primary">
              {data.first_name || (data.username ? `@${data.username}` : `#${data.id}`)}
            </div>
            {data.username && <div className="text-secondary">@{data.username}</div>}
            {data.created_at != null && (
              <div className="text-tertiary">
                {t.col.registered}: <RelativeTime value={data.created_at} />
              </div>
            )}
            {data.last_seen != null && (
              <div className="text-tertiary">
                {t.users.lastSeen}: <RelativeTime value={data.last_seen} />
              </div>
            )}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.06 — webhook config + yetkazish sog'ligi. GET /admin/bots/:id/webhook → { webhook }. */
export function BotWebhook() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotWebhook, Number(id))
  return (
    <Shell screenId="A5.06" title={t.bots.webhookTitle} botId={id} loading={loading} error={error} retry={retry}>
      {!data ? (
        <EmptyState title={t.bots.noWebhook} />
      ) : (
        <Card>
          <div className="space-y-1 text-sm">
            <div className="break-all font-mono text-xs text-secondary">{data.url}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-tertiary">
              <span>
                {t.bots.whPending}: <span className="text-primary">{data.pending_count}</span>
              </span>
              <span>
                {t.bots.whMaxConn}: <span className="text-primary">{data.max_connections}</span>
              </span>
              {data.ip_address && <span className="font-mono">{data.ip_address}</span>}
            </div>
            {data.last_error_message && (
              <div className="mt-1 flex items-start gap-1.5 text-danger">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>
                  {t.bots.whLastError}: {data.last_error_message}
                  {data.last_error_at != null && (
                    <>
                      {' · '}
                      <RelativeTime value={data.last_error_at} />
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.16 — Mini App'lar. GET /admin/bots/:id/apps → { apps }. */
export function BotApps() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotApps, Number(id))
  const apps = data ?? []
  return (
    <Shell screenId="A5.16" title={t.bots.appsTitle} botId={id} loading={loading} error={error} retry={retry}>
      {apps.length === 0 ? (
        <EmptyState title={t.bots.noApps} />
      ) : (
        <Card>
          <div className="space-y-2">
            {apps.map((a) => (
              <div key={a.id} className="rounded-md border border-border-subtle bg-surface-1 p-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-primary">{a.title}</span>
                  <code className="text-xs text-accent">/{a.short_name}</code>
                </div>
                {a.description && <div className="mt-0.5 text-xs text-secondary">{a.description}</div>}
                <div className="mt-0.5 break-all font-mono text-xs text-tertiary">{a.url}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.28 — business ulanishlar. GET /admin/bots/:id/business → { connections }. */
export function BotBusiness() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotBusiness, Number(id))
  const conns = data ?? []
  return (
    <Shell screenId="A5.28" title={t.bots.businessTitle} botId={id} loading={loading} error={error} retry={retry}>
      {conns.length === 0 ? (
        <EmptyState title={t.bots.noBusiness} />
      ) : (
        <Card>
          <div className="space-y-1">
            {conns.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm">
                <span className="text-primary">
                  {c.first_name || (c.username ? `@${c.username}` : `#${c.user_id}`)}
                </span>
                <span className={c.enabled ? 'text-accent' : 'text-tertiary'}>
                  {c.enabled ? t.bots.active : t.bots.disabled}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.29 — affiliate dastur. GET /admin/bots/:id/affiliate → { affiliate }. */
export function BotAffiliate() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotAffiliate, Number(id))
  return (
    <Shell screenId="A5.29" title={t.bots.affiliateTitle} botId={id} loading={loading} error={error} retry={retry}>
      {!data ? (
        <EmptyState title={t.bots.noAffiliate} />
      ) : (
        <Card>
          <div className="space-y-0.5 text-sm">
            <div className="text-tertiary">
              {t.bots.commission}:{' '}
              <span className="text-primary">{(data.commission_permille / 10).toFixed(1)}%</span>
            </div>
            {data.duration_months != null && (
              <div className="text-tertiary">
                {t.bots.duration}: <span className="text-primary">{data.duration_months}</span>
              </div>
            )}
            <div className={data.enabled ? 'text-accent' : 'text-tertiary'}>
              {data.enabled ? t.bots.active : t.bots.disabled}
            </div>
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.26/27 — o'yinlar + o'yinchi soni. GET /admin/bots/:id/games → { games }. */
export function BotGames() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotGames, Number(id))
  const games = data ?? []
  return (
    <Shell screenId="A5.26" title={t.bots.gamesTitle} botId={id} loading={loading} error={error} retry={retry}>
      {games.length === 0 ? (
        <EmptyState title={t.bots.noGames} />
      ) : (
        <Card>
          <div className="space-y-1">
            {games.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-primary">
                  {g.title} <code className="text-xs text-accent">/{g.short_name}</code>
                </span>
                <span className="shrink-0 text-tertiary">
                  {t.bots.players}: {g.players}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.04/05 — xabar-faollik + update statistikasi. GET /admin/bots/:id/stats → { stats }. */
export function BotStats() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotStats, Number(id))
  return (
    <Shell screenId="A5.04" title={t.bots.statsTitle} botId={id} loading={loading} error={error} retry={retry}>
      {!data ? (
        <EmptyState title={t.bots.statsTitle} />
      ) : (
        <Card>
          <div className="space-y-1 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-tertiary">{t.bots.statMsgs}</span>
              <span className="text-primary">
                {data.messages_total.toLocaleString()}{' '}
                <span className="text-xs text-tertiary">
                  (7d {data.messages_7d} · {t.bots.statToday} {data.messages_today})
                </span>
              </span>
            </div>
            {data.updates_total != null && (
              <div className="flex items-center justify-between">
                <span className="text-tertiary">{t.bots.statUpdates}</span>
                <span className="text-primary">
                  {data.updates_total.toLocaleString()}{' '}
                  <span className="text-xs text-tertiary">(7d {data.updates_7d ?? 0})</span>
                </span>
              </div>
            )}
          </div>
        </Card>
      )}
    </Shell>
  )
}

function toHex(c: number | null): string | null {
  if (c == null) return null
  return '#' + (c >>> 0).toString(16).padStart(6, '0').slice(-6)
}

function Swatch({ label, color }: { label: string; color: number | null }) {
  const hex = toHex(color)
  if (!hex) return null
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className="h-4 w-4 shrink-0 rounded border border-border-default"
        style={{ backgroundColor: hex }}
      />
      <span className="text-tertiary">{label}</span>
      <code className="text-xs text-secondary">{hex}</code>
    </div>
  )
}

/** A5.17 — Mini App tema sozlamalari. GET /admin/bots/:id/app-settings → { settings }. */
export function BotAppSettings() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotAppSettings, Number(id))
  return (
    <Shell
      screenId="A5.17"
      title={t.bots.appSettingsTitle}
      botId={id}
      loading={loading}
      error={error}
      retry={retry}
    >
      {!data ? (
        <EmptyState title={t.bots.noAppSettings} />
      ) : (
        <Card>
          <div className="space-y-1.5">
            <Swatch label={t.bots.themeBg} color={data.background_color} />
            <Swatch label={t.bots.themeHeader} color={data.header_color} />
            {data.placeholder_path && (
              <div className="break-all font-mono text-xs text-tertiary">{data.placeholder_path}</div>
            )}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.20 — Skrinshot/preview media. GET /admin/bots/:id/preview-media → { media }. */
export function BotPreviewMedia() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotPreviewMedia, Number(id))
  const media = data ?? []
  return (
    <Shell
      screenId="A5.20"
      title={t.bots.previewMediaTitle}
      botId={id}
      loading={loading}
      error={error}
      retry={retry}
    >
      {media.length === 0 ? (
        <EmptyState title={t.bots.noPreviewMedia} />
      ) : (
        <Card>
          <div className="flex flex-wrap gap-2">
            {media.map((m) => (
              <a
                key={m.id}
                href={m.url ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="group relative block h-20 w-32 shrink-0 overflow-hidden rounded-md border border-border-subtle bg-surface-1"
                title={`${m.media_type}${m.lang_code ? ` · ${m.lang_code}` : ''}`}
              >
                {m.thumb_url ? (
                  <img src={m.thumb_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-tertiary">
                    {m.media_type}
                  </div>
                )}
                {m.media_type === 'video' && (
                  <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[10px] text-white">
                    ▶
                  </span>
                )}
              </a>
            ))}
          </div>
        </Card>
      )}
    </Shell>
  )
}

/** A5.30 — Bot API kaliti monitoringi (prefix + rotatsiya; SEKRET emas). GET /admin/bots/:id/token → { token }. */
export function BotToken() {
  const t = useT()
  const { id = '' } = useParams()
  const { loading, data, error, retry } = useBotResource(fetchBotToken, Number(id))
  return (
    <Shell screenId="A5.30" title={t.bots.tokenTitle} botId={id} loading={loading} error={error} retry={retry}>
      {!data ? (
        <EmptyState title={t.bots.noToken} />
      ) : (
        <Card>
          <div className="space-y-0.5 text-sm">
            <div className="flex items-center gap-2">
              <code className="rounded bg-surface-1 px-1.5 py-0.5 font-mono text-xs text-secondary">
                {data.token_prefix ? `${data.token_prefix}••••••` : '—'}
              </code>
              {data.revoked ? (
                <span className="text-xs font-medium text-danger">{t.bots.tokenRevoked}</span>
              ) : (
                <span className="text-xs font-medium text-accent">{t.bots.tokenActive}</span>
              )}
            </div>
            {data.issued_at != null && (
              <div className="text-tertiary">
                {t.bots.tokenIssued}: <RelativeTime value={data.issued_at} />
              </div>
            )}
            {data.rotated_at != null && (
              <div className="text-tertiary">
                {t.bots.tokenRotated}: <RelativeTime value={data.rotated_at} />
              </div>
            )}
            {data.revoked && data.revoked_at != null && (
              <div className="text-danger">
                {t.bots.tokenRevokedAt}: <RelativeTime value={data.revoked_at} />
              </div>
            )}
          </div>
        </Card>
      )}
    </Shell>
  )
}
